
import { supabase } from "@/lib/supabase";

export const extractEmail = (fullEmail: string): string => {
  const matches = fullEmail.match(/<(.+?)>/);
  if (matches && matches[1]) {
    return matches[1];
  }
  return fullEmail;
};

export const fetchEmailAccount = async (senderEmail: string) => {
  console.log("Fetching account details for:", senderEmail);
  
  const { data: account, error } = await supabase
    .from('email_accounts')
    .select('*')
    .eq('email', senderEmail)
    .maybeSingle();

  if (error) {
    console.error("Error fetching account details:", error);
    throw new Error('Failed to fetch email account details. Please try again.');
  }

  if (!account) {
    console.error("No account found for email:", senderEmail);
    throw new Error(`No email account found for ${senderEmail}. Please make sure you've connected this email account in Settings.`);
  }

  return account;
};

// Helper function to create a boundary for multipart messages
const generateBoundary = () => {
  return `----=_NextPart_${Math.random().toString(36).substring(2, 15)}`;
};

// Helper function to read a file as a base64 string
const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Extract the base64 part of the data URL
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const handleGmailSend = async (
  token: string | null,
  emailContent: string,
  recipientEmail: string,
  options: { 
    subject?: string; 
    cc?: string; 
    bcc?: string;
    attachments?: File[];
  }
) => {
  if (!token) {
    console.error("No valid Gmail token found");
    throw new Error('Please reconnect your Gmail account.');
  }

  // Debug log - original subject
  console.log("Original subject before encoding:", options.subject);

  const boundary = generateBoundary();
  
  // Create headers with explicit encoding information
  const headers = [
    `MIME-Version: 1.0`,
    `Content-Type: multipart/mixed; boundary="${boundary}"`,
    `To: ${recipientEmail}`,
  ];

  if (options.cc) {
    console.log("Adding CC:", options.cc);
    headers.push(`Cc: ${options.cc}`);
  }

  if (options.bcc) {
    console.log("Adding BCC:", options.bcc);
    headers.push(`Bcc: ${options.bcc}`);
  }

  // Create a properly formatted RFC5322 message with UTF-8 subject
  // Instead of including the subject in headers, add it properly with encoded-word syntax
  const subjectHeader = `Subject: =?UTF-8?B?${btoa(unescape(encodeURIComponent(options.subject || '')))}?=`;
  headers.push(subjectHeader);
  
  console.log("Encoded subject header:", subjectHeader);

  // Start building the email parts
  let emailParts = [
    headers.join('\r\n'),
    '',
    `--${boundary}`,
    'Content-Type: text/html; charset="UTF-8"',
    'Content-Transfer-Encoding: 8bit',
    '',
    emailContent,
  ];

  // Add attachments if present
  if (options.attachments && options.attachments.length > 0) {
    console.log(`Processing ${options.attachments.length} attachments`);
    
    for (const file of options.attachments) {
      console.log(`Adding attachment: ${file.name} (${file.size} bytes, ${file.type})`);
      
      try {
        const base64Content = await readFileAsBase64(file);
        
        emailParts.push(
          `--${boundary}`,
          `Content-Type: ${file.type || 'application/octet-stream'}; name="${file.name}"`,
          'Content-Transfer-Encoding: base64',
          `Content-Disposition: attachment; filename="${file.name}"`,
          '',
          // Base64 data should be wrapped at 76 chars per line
          base64Content.replace(/.{76}/g, '$&\r\n')
        );
      } catch (error) {
        console.error(`Error processing attachment ${file.name}:`, error);
        throw new Error(`Failed to process attachment: ${file.name}`);
      }
    }
  }

  // Close the MIME multipart message
  emailParts.push(`--${boundary}--`);

  // Join all parts with CRLF
  const email = emailParts.join('\r\n');
  
  // Use TextEncoder for proper Unicode handling of the entire message
  const encoder = new TextEncoder();
  const bytes = encoder.encode(email);
  const base64EncodedEmail = btoa(
    Array.from(bytes)
      .map(byte => String.fromCharCode(byte))
      .join('')
  )
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  console.log("Sending email with UTF-8 encoding and attachments...");
  
  const response = await fetch('https://www.googleapis.com/gmail/v1/users/me/messages/send', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ raw: base64EncodedEmail })
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error("Gmail API error:", errorData);
    
    if (errorData.error?.code === 401) {
      await handleInvalidGmailToken(recipientEmail);
      throw new Error('Authentication expired. Please reconnect your Gmail account.');
    }
    
    throw new Error(errorData.error?.message || 'Failed to send email');
  }

  return response.json();
};

export const handleInvalidGmailToken = async (email: string) => {
  const { error: deleteError } = await supabase
    .from('email_accounts')
    .delete()
    .eq('email', email);
    
  if (deleteError) {
    console.error("Error deleting invalid account:", deleteError);
  }
};

export const handleImapSend = async (
  account: any, 
  emailContent: string, 
  recipientEmail: string, 
  options: { 
    subject?: string; 
    cc?: string; 
    bcc?: string;
    attachments?: File[];
  }
) => {
  console.log("Sending via IMAP for account:", account.email);
  console.log("IMAP subject (original):", options.subject);
  
  // For file attachments, we need to process them for the edge function
  const processedAttachments = options.attachments?.length 
    ? await Promise.all(options.attachments.map(async (file) => {
        try {
          const base64Content = await readFileAsBase64(file);
          return {
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            content: base64Content
          };
        } catch (error) {
          console.error(`Error processing attachment ${file.name}:`, error);
          throw new Error(`Failed to process attachment: ${file.name}`);
        }
      }))
    : undefined;
  
  // For IMAP, we'll explicitly tell the function to handle everything in UTF-8
  const { data, error } = await supabase.functions.invoke('email-operations', {
    body: {
      action: 'send',
      user: account.email,
      password: account.credentials.password,
      smtpHost: account.credentials.smtp.host,
      smtpPort: account.credentials.smtp.port,
      to: recipientEmail,
      subject: options.subject,
      subjectEncoding: 'base64', // Signal to the backend to use base64 encoding for subject
      cc: options.cc,
      bcc: options.bcc,
      html: emailContent,
      encoding: 'utf-8', // Explicitly specify UTF-8 encoding
      useInternationalHeaders: true, // Signal to use encoded-word headers for intl chars
      attachments: processedAttachments
    }
  });

  if (error) throw error;
  if (!data.success) throw new Error(data.message || 'Failed to send email');

  return data;
};
